import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link, useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PageGuide from "components/admin/PageGuide";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import GiftEditor, { GiftPreview } from "components/admin/GiftEditor";
import { useConfirm } from "components/admin/Feedback";
import useConfirmChanges from "components/admin/confirmChanges";
import PlayerSearch from "components/admin/PlayerSearch";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatNumber } from "utils/format";

/** TowerManager.KIND_* */
const KINDS = {
  0: { label: "Thường", color: "default" },
  1: { label: "Tinh anh", color: "warning" },
  2: { label: "Boss", color: "error" },
};

/** TowerManager.RESULT_* */
const RESULTS = { 0: "Qua tầng", 1: "Thua", 2: "Hết giờ", 3: "Rời tháp" };

const ROLE_COLORS = { Boss: "error", "Tinh anh": "warning", "Quái thường": "secondary" };

/** Màu chip độ khó theo TowerManager.MODE_* */
const MODE_COLORS = { 0: "success", 1: "warning", 2: "error" };

/** Giống TowerManager.DurationText: "1 giờ 05 phút 09 giây" */
function formatDuration(ms) {
  if (ms == null) return "-";
  const total = Math.floor(Math.max(0, ms) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor(total / 60) % 60;
  const s = String(total % 60).padStart(2, "0");
  if (h > 0) return `${h} giờ ${String(m).padStart(2, "0")} phút ${s} giây`;
  if (m > 0) return `${m} phút ${s} giây`;
  return `${total % 60} giây`;
}

/** Chỉ số thật của từng nhóm quái trong tầng (server dựng bằng đúng hàm sinh quái của tháp) */
function MobStats({ stats, compact }) {
  if (!stats?.length) return "-";
  return (
    <MDBox display="flex" flexDirection="column" gap={1}>
      {stats.map((s) => (
        <MDBox key={s.role}>
          <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
            <Chip
              size="small"
              color={ROLE_COLORS[s.role] || "default"}
              label={`${s.role}${s.count > 1 ? ` x${s.count}` : ""}`}
            />
            <MDTypography variant="caption" fontWeight="medium">
              cấp {s.lvl} · {s.archetype}
            </MDTypography>
          </MDBox>
          <MDTypography variant="caption" display="block" fontWeight="bold">
            HP {formatNumber(s.hp)} · ATK {formatNumber(s.atk)} · DEF {formatNumber(s.def)} · MP{" "}
            {formatNumber(s.mp)}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block">
            Chí mạng nền {s.crit}% (x{s.critDamage}%) · Né {s.skip}% · Chính xác {s.accuracy}%
            {!compact &&
              ` · Sức mạnh ${formatNumber(s.str)} · Trí tuệ ${formatNumber(
                s.int
              )} · Nhanh nhẹn ${formatNumber(s.agi)}`}
          </MDTypography>
          {s.effects.length > 0 && (
            <MDTypography variant="caption" color="error" display="block">
              {s.effects.join(" · ")}
            </MDTypography>
          )}
          <MDTypography variant="caption" color="text" display="block">
            {s.pets.map((p) => `${p.name} (${p.element})`).join(", ")}
          </MDTypography>
        </MDBox>
      ))}
    </MDBox>
  );
}

MobStats.defaultProps = { stats: [], compact: false };

MobStats.propTypes = {
  stats: PropTypes.arrayOf(PropTypes.object),
  compact: PropTypes.bool,
};

/** Sửa một tầng: quái, hệ số, giờ, quà vượt tầng lần đầu */
function FloorDialog({ floor, modeName, archetypes, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState(() => ({ ...floor }));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const isHead = Number(form.kind) !== 0;
  const [preview, setPreview] = useState({ stats: floor.stats, error: null });

  const body = () => ({
    kind: Number(form.kind),
    mobCount: Number(form.mobCount),
    mobLvl: Number(form.mobLvl),
    hpMul: Number(form.hpMul),
    statMul: Number(form.statMul),
    headHpMul: Number(form.headHpMul),
    headStatMul: Number(form.headStatMul),
    archetypeId: Number(form.archetypeId),
    petTemplateId: Number(form.petTemplateId),
    name: form.name || "",
    timeLimit: Number(form.timeLimit),
    gift: form.gift || [],
  });

  // Xem trước chỉ số theo số đang sửa, chờ người dùng ngừng gõ 400ms
  const statKey = [
    form.kind,
    form.mobCount,
    form.mobLvl,
    form.hpMul,
    form.statMul,
    form.headHpMul,
    form.headStatMul,
    form.archetypeId,
    form.petTemplateId,
  ].join("|");
  useEffect(() => {
    let alive = true;
    const timer = setTimeout(async () => {
      try {
        const stats = await api.post(
          `/api/admin/tower/floors/${floor.mode}/${floor.floor}/preview`,
          body()
        );
        if (alive) setPreview({ stats, error: null });
      } catch (e) {
        if (alive) setPreview((p) => ({ ...p, error: e.message }));
      }
    }, 400);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [statKey]);

  const submit = async () => {
    const yes = await ask({
      title: `Lưu tầng ${floor.floor} - ${modeName}`,
      before: floor,
      after: form,
      fields: [
        ["kind", "Loại tầng"],
        ["mobCount", "Số quái"],
        ["mobLvl", "Cấp quái"],
        ["hpMul", "Hệ số máu"],
        ["statMul", "Hệ số chỉ số"],
        ["headHpMul", "Hệ số máu tinh anh / boss"],
        ["headStatMul", "Hệ số chỉ số tinh anh / boss"],
        ["archetypeId", "Mẫu quái"],
        ["petTemplateId", "Hình boss (mã pet)"],
        ["name", "Tên boss"],
        ["timeLimit", "Giới hạn thời gian"],
        ["gift", "Quà vượt tầng"],
      ],
      notes: ["Áp dụng từ lần vào tầng sau; người đang đánh tầng này giữ bản cũ."],
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.put(`/api/admin/tower/floors/${floor.mode}/${floor.floor}`, body()),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  const num = (key, label, props = {}) => (
    <Grid item xs={6} md={3}>
      <MDInput
        fullWidth
        type="number"
        label={label}
        value={form[key]}
        inputProps={{ step: "any", ...props }}
        onChange={(e) => set({ [key]: e.target.value })}
      />
    </Grid>
  );

  return (
    <ActionDialog
      open
      maxWidth="md"
      title={`Tầng ${floor.floor} - ${modeName}`}
      description="Sửa áp dụng từ lần vào tầng sau. Tầng tinh anh: con đầu là tinh anh (hệ số tinh anh), các con còn lại là quái thường dùng nguyên mẫu Thường. Tầng boss: mọi quái đều là boss."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <MDInput
            select
            fullWidth
            label="Loại tầng"
            value={form.kind}
            onChange={(e) => set({ kind: Number(e.target.value) })}
            SelectProps={{ sx: { height: 40 } }}
          >
            {Object.entries(KINDS).map(([k, v]) => (
              <MenuItem key={k} value={Number(k)}>
                {v.label}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        {num("mobCount", Number(form.kind) === 2 ? "Số boss" : "Số quái", { min: 1, max: 6 })}
        {num("mobLvl", "Cấp quái (petrealm_mob)", { min: 1 })}
        {num("timeLimit", "Thời gian (giây)", { min: 30 })}
        {num("hpMul", "Hệ số máu quái thường")}
        {num("statMul", "Hệ số chỉ số quái thường")}
        {num("headHpMul", "Hệ số máu tinh anh / boss")}
        {num("headStatMul", "Hệ số chỉ số tinh anh / boss")}
        <Grid item xs={12} md={4}>
          <MDInput
            select
            fullWidth
            label={isHead ? "Nguyên mẫu tinh anh / boss" : "Nguyên mẫu quái"}
            value={form.archetypeId}
            onChange={(e) => set({ archetypeId: Number(e.target.value) })}
            SelectProps={{ sx: { height: 40 } }}
          >
            {!archetypes.some((a) => a.id === 0) && <MenuItem value={0}>Thường</MenuItem>}
            {archetypes.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.name} (#{a.id})
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        <Grid item xs={6} md={4}>
          <MDInput
            fullWidth
            type="number"
            label="Hình boss (id pet, 0 = quái theo tầng)"
            value={form.petTemplateId}
            onChange={(e) => set({ petTemplateId: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <MDInput
            fullWidth
            label="Tên boss"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
        Chỉ số quái khi vào tầng (xem trước, chưa lưu)
      </MDTypography>
      {preview.error && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {preview.error}
        </Alert>
      )}
      <MobStats stats={preview.stats} />
      <Divider sx={{ my: 2 }} />
      <GiftEditor
        value={form.gift}
        onChange={(gift) => set({ gift })}
        label="Quà vượt tầng lần đầu trong tháng (gửi qua thư)"
        randomNote="Danh hiệu có hạn tự đổi thành hạn tới 0h ngày 1 tháng sau lúc gửi."
      />
    </ActionDialog>
  );
}

FloorDialog.propTypes = {
  floor: PropTypes.shape({
    mode: PropTypes.number,
    floor: PropTypes.number,
    stats: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  modeName: PropTypes.string.isRequired,
  archetypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

/** Thêm / sửa quà top tháng */
function RewardDialog({ reward, mode, modes, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState(() =>
    reward ? { ...reward } : { mode, rankFrom: 1, rankTo: 1, gift: [[2, 185, 1]], enabled: true }
  );
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    const modeText = (v) => modes.find((m) => m.mode === Number(v))?.name || v;
    const yes = await ask({
      title: reward ? "Lưu quà top tháng" : "Thêm quà top tháng",
      isNew: !reward,
      before: reward,
      after: form,
      fields: [
        ["mode", "Độ khó tối thiểu", { text: modeText }],
        ["rankFrom", "Từ hạng"],
        ["rankTo", "Đến hạng"],
        ["gift", "Quà"],
        ["enabled", "Đang áp dụng"],
      ],
      notes: [
        !reward &&
          `Hạng ${form.rankFrom} - ${form.rankTo} (đã vào ${modeText(
            form.mode
          )} trở lên) nhận quà lúc chốt tháng.`,
        "Áp dụng cho lần chốt tháng tiếp theo; quà tháng đã chốt không đổi.",
      ],
    });
    if (!yes) return;
    const body = {
      mode: Number(form.mode),
      rankFrom: Number(form.rankFrom),
      rankTo: Number(form.rankTo),
      gift: form.gift,
      enabled: Boolean(form.enabled),
    };
    const { ok } = await run(
      () =>
        reward
          ? api.put(`/api/admin/tower/rewards/${reward.id}`, body)
          : api.post("/api/admin/tower/rewards", body),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      maxWidth="md"
      title={`${reward ? "Sửa" : "Thêm"} quà top tháng`}
      description="Gửi một thư cho mỗi người trong khoảng hạng của BXH chung lúc chốt tháng (0h ngày 1), nếu người đó đã vào được độ khó tối thiểu. Một hạng khớp nhiều mốc thì nhận mốc có độ khó cao nhất. Danh hiệu có hạn tự đổi thành hạn tới hết tháng kế tiếp."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <MDInput
            select
            fullWidth
            label="Độ khó tối thiểu"
            value={form.mode}
            onChange={(e) => set({ mode: Number(e.target.value) })}
            SelectProps={{ sx: { height: 40 } }}
          >
            {modes.map((m) => (
              <MenuItem key={m.mode} value={m.mode}>
                {m.name}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        <Grid item xs={6} md={2}>
          <MDInput
            fullWidth
            type="number"
            label="Từ hạng"
            value={form.rankFrom}
            onChange={(e) => set({ rankFrom: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <MDInput
            fullWidth
            type="number"
            label="Đến hạng"
            value={form.rankTo}
            onChange={(e) => set({ rankTo: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.enabled)}
                onChange={(e) => set({ enabled: e.target.checked })}
              />
            }
            label="Đang áp dụng"
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <GiftEditor value={form.gift} onChange={(gift) => set({ gift })} label="Quà gửi vào thư" />
    </ActionDialog>
  );
}

RewardDialog.defaultProps = { reward: null };

RewardDialog.propTypes = {
  reward: PropTypes.shape({ id: PropTypes.number }),
  mode: PropTypes.number.isRequired,
  modes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

/** Xem / sửa tiến độ tháng này của một người chơi */
function PlayerCard({ canEdit, modes }) {
  const [who, setWho] = useState(null);
  const userId = who?.userId || "";
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState({});
  const [run, busy] = useRunner();

  const load = async (id = userId) => {
    if (!id) return;
    setError(null);
    try {
      const res = await api.get(`/api/admin/tower/players/${Number(id)}`);
      setData(res);
      setEdit(Object.fromEntries(res.progress.map((p) => [p.mode, p.maxFloor])));
    } catch (e) {
      setData(null);
      setError(e.message);
    }
  };

  const save = async (mode) => {
    const p = data.progress.find((x) => x.mode === mode);
    const to = Number(edit[mode]);
    const yes = await confirm({
      title: `Sửa tầng ${p.name}`,
      message: [
        `${who?.name || `Tài khoản ${userId}`}: tầng cao nhất ${p.maxFloor} → ${to} (tháng này).`,
        "",
        to > p.maxFloor
          ? "• Không gửi quà các tầng bị bỏ qua — muốn bù quà thì gửi qua Thư & quà."
          : "• Hạ tầng: người chơi phải đánh lại từ tầng mới, quà đã nhận không bị thu.",
        "• Thứ hạng tháng thay đổi theo tầng mới.",
      ].join("\n"),
      color: "warning",
      confirmText: "Sửa tầng",
    });
    if (!yes) return;
    const { ok } = await run(
      () =>
        api.put(`/api/admin/tower/players/${Number(userId)}`, {
          mode,
          maxFloor: Number(edit[mode]),
        }),
      (text) => text
    );
    if (ok) load();
  };

  return (
    <PageCard
      title="Tiến độ người chơi"
      subtitle="Tháng hiện tại; sửa tầng không gửi quà các tầng bị bỏ qua"
      color="dark"
    >
      <MDBox mb={2}>
        <PlayerSearch
          onPick={(person) => {
            setWho(person);
            load(person.userId);
          }}
        />
        {who?.name && (
          <MDTypography variant="button" display="block" mt={1}>
            <Link to={`/players/${who.userId}`}>{who.name}</Link>
          </MDTypography>
        )}
      </MDBox>
      {error && <Alert severity="error">{error}</Alert>}
      {data && (
        <MDBox>
          {data.progress.map((p) => (
            <MDBox key={p.mode} display="flex" alignItems="center" gap={1} py={0.5}>
              <MDTypography variant="button" sx={{ minWidth: 110 }}>
                {p.name}
              </MDTypography>
              <MDInput
                type="number"
                size="small"
                value={edit[p.mode] ?? 0}
                disabled={!canEdit}
                inputProps={{ min: 0, max: p.floors }}
                onChange={(e) => setEdit((x) => ({ ...x, [p.mode]: e.target.value }))}
                sx={{ width: 90 }}
              />
              <MDTypography variant="caption" color="text">
                / {p.floors} {p.reachedAt ? `· ${formatDate(p.reachedAt)}` : ""}
                {p.clearMs ? ` · ${formatDuration(p.clearMs)}` : ""}
              </MDTypography>
              {canEdit && (
                <MDButton
                  size="small"
                  variant="text"
                  color="warning"
                  disabled={busy || Number(edit[p.mode]) === p.maxFloor}
                  onClick={() => save(p.mode)}
                >
                  <Icon>save</Icon>&nbsp;Lưu
                </MDButton>
              )}
            </MDBox>
          ))}
          <Divider sx={{ my: 1 }} />
          <MDTypography variant="button" fontWeight="medium">
            30 lượt gần nhất
          </MDTypography>
          <AdminTable
            rows={data.logs.map((l, i) => ({ ...l, key: i }))}
            rowKey="key"
            emptyText="Chưa vào tháp"
            columns={[
              { key: "createdAt", label: "Lúc", render: (l) => formatDate(l.createdAt) },
              {
                key: "floor",
                label: "Tầng",
                render: (l) =>
                  `${modes[l.mode]?.name || l.mode} ${l.floor}${l.practice ? " (đánh lại)" : ""}`,
              },
              { key: "result", label: "Kết quả", render: (l) => RESULTS[l.result] || l.result },
              {
                key: "durationMs",
                label: "Thời gian",
                align: "right",
                render: (l) => `${Math.round(l.durationMs / 1000)} giây`,
              },
            ]}
          />
        </MDBox>
      )}
    </PageCard>
  );
}

PlayerCard.propTypes = {
  canEdit: PropTypes.bool.isRequired,
  modes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

// Tab ghi lên URL (?tab=). Danh sách 100 tầng rất dài nên tách riêng.
const PAGE_TABS = [
  { key: "overview", label: "Tổng quan & BXH" },
  { key: "floors", label: "Các tầng" },
  { key: "rewards", label: "Quà top & người chơi" },
];
const TAB_SX = { flex: { xs: "0 0 auto", md: "1 1 auto" }, px: 1.5, whiteSpace: "nowrap" };

function Tower() {
  const canEdit = useAuth().can(PERMISSIONS.TOWER_EDIT);
  const [params, setParams] = useSearchParams();
  const tab = PAGE_TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "overview";
  const pickTab = (v) => setParams(v === "overview" ? {} : { tab: v }, { replace: true });
  const [mode, setMode] = useState(0);
  const [monthKey, setMonthKey] = useState(0);
  const overview = useFetch(
    () => api.get(`/api/admin/tower?mode=${mode}&monthKey=${monthKey}`),
    [mode, monthKey]
  );
  const [editingFloor, setEditingFloor] = useState(null);
  const [editingReward, setEditingReward] = useState(null);
  const [headOnly, setHeadOnly] = useState(false);
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const data = overview.data;
  const modes = data?.modes || [];
  const modeName = modes[mode]?.name || "";
  const month = data?.months.find((m) => m.monthKey === data.monthKey);
  const reload = () => overview.reload(true);

  const toggle = async (enabled) => {
    const yes = await confirm({
      title: enabled ? "Mở lại tháp" : "Tạm đóng tháp",
      message: enabled
        ? "Người chơi vào tháp lại được ngay ở NPC Đấu trường."
        : "Người chơi không vào tháp được nữa. Ai đang ở trong tháp vẫn đánh xong tầng hiện tại. Bảng hạng tháng và lịch chốt không đổi.",
      color: enabled ? "info" : "warning",
      confirmText: enabled ? "Mở tháp" : "Tạm đóng",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.put("/api/admin/tower/config", { enabled }),
      (t) => t
    );
    if (ok) reload();
  };

  const pendingMonths = (data?.months || []).filter(
    (m) => m.monthKey !== data.currentMonthKey && !m.settled
  );
  const settle = async () => {
    const yes = await confirm({
      title: "Chốt các tháng đã kết thúc",
      message: [
        pendingMonths.length > 0
          ? `Chốt ${pendingMonths
              .map((m) => m.text)
              .join(", ")}: gửi quà top cho người đủ điều kiện.`
          : "Không thấy tháng nào chờ chốt — máy chủ sẽ kiểm tra lại, không gửi gì thêm.",
        "",
        "Bình thường máy chủ tự chốt lúc 0h ngày 1. Chỉ cần bấm khi máy chủ tắt đúng lúc đó.",
        "Tháng đã chốt không chốt lại, không gửi trùng quà.",
      ].join("\n"),
      color: "warning",
      confirmText: "Chốt",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.post("/api/admin/tower/settle"),
      (t) => t
    );
    if (ok) reload();
  };

  const removeReward = async (r) => {
    const yes = await confirm({
      title: "Xoá quà top",
      message: `Xoá quà hạng ${r.rankFrom} - ${r.rankTo}? Muốn ngừng tạm thời thì tắt "Đang áp dụng".`,
      color: "error",
      confirmText: "Xoá",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.del(`/api/admin/tower/rewards/${r.id}`),
      (t) => t
    );
    if (ok) reload();
  };

  const rewards = (data?.rewards || [])
    .slice()
    .sort((a, b) => a.rankFrom - b.rankFrom || b.mode - a.mode);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="tower" />
        {overview.error && <Alert severity="error">{overview.error}</Alert>}
        <Tabs
          value={tab}
          onChange={(e, v) => pickTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3, maxWidth: 720 }}
        >
          {PAGE_TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} sx={TAB_SX} />
          ))}
        </Tabs>
        <Grid container spacing={3}>
          {tab === "overview" && (
            <>
              <Grid item xs={12} lg={4}>
                <PageCard
                  title="Tháp thử thách"
                  subtitle="NPC Đấu trường, lựa chọn 10. Reset 0h ngày 1 hằng tháng"
                  color="primary"
                  actions={
                    <MDButton size="small" color="white" onClick={() => overview.reload()}>
                      <Icon>refresh</Icon>
                    </MDButton>
                  }
                >
                  {data && (
                    <MDBox>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Switch
                            checked={data.enabled}
                            disabled={!canEdit || busy}
                            onChange={(e) => toggle(e.target.checked)}
                          />
                        }
                        label={data.enabled ? "Đang mở" : "Đang tạm đóng"}
                      />
                      <MDInput
                        select
                        fullWidth
                        label="Tháng"
                        value={data.monthKey}
                        onChange={(e) => setMonthKey(Number(e.target.value))}
                        SelectProps={{ sx: { height: 40 } }}
                        sx={{ mt: 2 }}
                      >
                        {data.months.map((m) => (
                          <MenuItem key={m.monthKey} value={m.monthKey}>
                            {m.text}
                            {m.monthKey === data.currentMonthKey ? " (hiện tại)" : ""}
                            {m.settled ? " · đã chốt" : ""}
                          </MenuItem>
                        ))}
                      </MDInput>
                      <MDBox mt={2}>
                        {modes.map((m) => (
                          <MDBox
                            key={m.mode}
                            display="flex"
                            justifyContent="space-between"
                            py={0.5}
                          >
                            <MDTypography variant="button" color="text">
                              {m.name} ({m.floors} tầng)
                            </MDTypography>
                            <MDTypography variant="button" fontWeight="medium">
                              {formatNumber(m.players)} người đã qua tầng
                            </MDTypography>
                          </MDBox>
                        ))}
                        {month?.settled && (
                          <MDTypography variant="caption" color="text" display="block" mt={1}>
                            Đã chốt lúc {formatDate(month.settledAt)}
                          </MDTypography>
                        )}
                      </MDBox>
                      {canEdit && (
                        <MDButton
                          sx={{ mt: 2 }}
                          size="small"
                          variant="outlined"
                          color="secondary"
                          disabled={busy}
                          onClick={settle}
                        >
                          <Icon>event_available</Icon>&nbsp;Chốt các tháng đã kết thúc
                          {pendingMonths.length > 0 ? ` (${pendingMonths.length})` : ""}
                        </MDButton>
                      )}
                    </MDBox>
                  )}
                </PageCard>
              </Grid>
              <Grid item xs={12} lg={8}>
                <PageCard
                  title={`BXH tháng ${month?.text || ""}`}
                  subtitle={
                    month?.settled
                      ? "Top đã chốt và quà đã gửi"
                      : "Xếp theo độ khó cao nhất, rồi tầng, rồi tổng thời gian qua tầng (ngắn hơn đứng trên). Quà top là quà dự kiến nếu chốt lúc này"
                  }
                  color="info"
                  noPadding
                >
                  <AdminTable
                    loading={overview.loading && !data}
                    rows={data?.ranking || []}
                    rowKey="key"
                    emptyText="Chưa ai qua tầng nào"
                    columns={[
                      { key: "rank", label: "Hạng", align: "center" },
                      {
                        key: "name",
                        label: "Người chơi",
                        render: (r) => <Link to={`/players/${r.userId}`}>{r.name}</Link>,
                      },
                      {
                        key: "mode",
                        label: "Độ khó",
                        render: (r) => (
                          <Chip
                            size="small"
                            color={MODE_COLORS[r.mode] || "default"}
                            label={r.modeName}
                          />
                        ),
                      },
                      { key: "maxFloor", label: "Tầng", align: "center" },
                      {
                        key: "totalMs",
                        label: "Tổng thời gian",
                        render: (r) => formatDuration(r.totalMs),
                      },
                      {
                        key: "reachedAt",
                        label: "Đạt lúc",
                        render: (r) => formatDate(r.reachedAt),
                      },
                      {
                        key: "gift",
                        label: "Quà top",
                        wrap: true,
                        render: (r) =>
                          r.giftLines?.length ? <GiftPreview lines={r.giftLines} /> : "-",
                      },
                    ]}
                  />
                </PageCard>
              </Grid>
            </>
          )}
          {tab === "floors" && (
            <>
              <Grid item xs={12}>
                <PageCard
                  title={`Các tầng - ${modeName}`}
                  subtitle="Chỉ số là số thật của quái khi vào tầng. Quà là quà vượt tầng lần đầu trong tháng, gửi qua thư (hạn 30 ngày)"
                  color="warning"
                  noPadding
                  actions={
                    <FormControlLabel
                      sx={{ m: 0, color: "white" }}
                      control={
                        <Switch
                          size="small"
                          checked={headOnly}
                          onChange={(e) => setHeadOnly(e.target.checked)}
                        />
                      }
                      label={
                        <MDTypography variant="caption" color="white">
                          Chỉ tầng boss / tinh anh
                        </MDTypography>
                      }
                    />
                  }
                >
                  <MDBox px={2}>
                    <Tabs value={mode} onChange={(e, v) => setMode(v)}>
                      {modes.map((m) => (
                        <Tab key={m.mode} value={m.mode} label={m.name} />
                      ))}
                    </Tabs>
                  </MDBox>
                  <AdminTable
                    loading={overview.loading && !data}
                    rows={(data?.floors || []).filter((f) => !headOnly || f.kind !== 0)}
                    rowKey="floor"
                    emptyText="Chưa có tầng nào"
                    columns={[
                      {
                        key: "floor",
                        label: "Tầng",
                        align: "center",
                        render: (f) => (
                          <MDTypography variant="button" fontWeight="bold">
                            {f.floor}
                          </MDTypography>
                        ),
                      },
                      {
                        key: "kind",
                        label: "Loại",
                        render: (f) => (
                          <Chip
                            size="small"
                            color={KINDS[f.kind]?.color}
                            label={KINDS[f.kind]?.label}
                          />
                        ),
                      },
                      {
                        key: "mobs",
                        label: "Quái",
                        wrap: true,
                        render: (f) => (
                          <MDBox>
                            {f.kind === 2 ? `${f.mobCount} boss` : `${f.mobCount} quái`} · cấp{" "}
                            {f.mobLvl}
                            {f.kind === 2 && (
                              <MDTypography variant="caption" display="block">
                                {f.name} ({f.petName || `pet ${f.petTemplateId}`})
                              </MDTypography>
                            )}
                            <MDTypography variant="caption" color="text" display="block">
                              {f.kind !== 0
                                ? `${KINDS[f.kind].label}: máu x${f.headHpMul}, chỉ số x${
                                    f.headStatMul
                                  }`
                                : `máu x${f.hpMul}, chỉ số x${f.statMul}`}
                              {f.kind === 1 ? ` · quái thường x${f.hpMul}` : ""}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                      {
                        key: "stats",
                        label: "Chỉ số quái",
                        wrap: true,
                        render: (f) => <MobStats stats={f.stats} compact />,
                      },
                      {
                        key: "timeLimit",
                        label: "Giờ",
                        align: "center",
                        render: (f) => `${f.timeLimit}s`,
                      },
                      {
                        key: "gift",
                        label: "Quà",
                        wrap: true,
                        render: (f) => <GiftPreview lines={f.lines} />,
                      },
                      canEdit && {
                        key: "actions",
                        label: "",
                        align: "right",
                        render: (f) => (
                          <MDButton
                            size="small"
                            variant="text"
                            color="warning"
                            onClick={() => setEditingFloor(f)}
                          >
                            <Icon>edit</Icon>&nbsp;Sửa
                          </MDButton>
                        ),
                      },
                    ].filter(Boolean)}
                  />
                </PageCard>
              </Grid>
            </>
          )}
          {tab === "rewards" && (
            <>
              <Grid item xs={12} lg={7}>
                <PageCard
                  title="Quà top tháng"
                  subtitle="Chốt lúc 0h ngày 1, gửi thư cho top tháng trước đã vào được độ khó tối thiểu của mốc"
                  color="warning"
                  noPadding
                  actions={
                    canEdit && (
                      <MDButton size="small" color="white" onClick={() => setEditingReward("new")}>
                        <Icon>add</Icon>&nbsp;Thêm mốc
                      </MDButton>
                    )
                  }
                >
                  <AdminTable
                    rows={rewards}
                    emptyText="Chưa có quà top"
                    columns={[
                      {
                        key: "rank",
                        label: "Hạng",
                        render: (r) =>
                          r.rankFrom === r.rankTo
                            ? `#${r.rankFrom}`
                            : `#${r.rankFrom} - ${r.rankTo}`,
                      },
                      {
                        key: "mode",
                        label: "Độ khó tối thiểu",
                        render: (r) => (
                          <Chip
                            size="small"
                            color={MODE_COLORS[r.mode] || "default"}
                            label={modes[r.mode]?.name || r.mode}
                          />
                        ),
                      },
                      {
                        key: "gift",
                        label: "Quà",
                        wrap: true,
                        render: (r) => <GiftPreview lines={r.lines} />,
                      },
                      {
                        key: "enabled",
                        label: "Trạng thái",
                        render: (r) => (
                          <Chip
                            size="small"
                            color={r.enabled ? "success" : "default"}
                            label={r.enabled ? "Đang áp dụng" : "Tắt"}
                          />
                        ),
                      },
                      canEdit && {
                        key: "actions",
                        label: "",
                        align: "right",
                        render: (r) => (
                          <MDBox display="flex" justifyContent="flex-end">
                            <MDButton
                              size="small"
                              variant="text"
                              color="warning"
                              onClick={() => setEditingReward(r)}
                            >
                              <Icon>edit</Icon>
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="text"
                              color="error"
                              onClick={() => removeReward(r)}
                            >
                              <Icon>delete</Icon>
                            </MDButton>
                          </MDBox>
                        ),
                      },
                    ].filter(Boolean)}
                  />
                </PageCard>
              </Grid>
              <Grid item xs={12} lg={5}>
                <PlayerCard canEdit={canEdit} modes={modes} />
              </Grid>
            </>
          )}
        </Grid>
      </MDBox>
      {editingFloor && (
        <FloorDialog
          floor={editingFloor}
          modeName={modeName}
          archetypes={data?.archetypes || []}
          onClose={() => setEditingFloor(null)}
          onDone={reload}
        />
      )}
      {editingReward && (
        <RewardDialog
          reward={editingReward === "new" ? null : editingReward}
          mode={mode}
          modes={modes}
          onClose={() => setEditingReward(null)}
          onDone={reload}
        />
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default Tower;
